import React, { ChangeEvent, useEffect, useState, useRef } from 'react';
import './Chat.page.css';
import '../../ui/pageScaffolds/SidePanelScaffold/SidePanelScaffold.css';
import '../../ui/Shared.css';
import { IoMdSend, IoMdClose } from 'react-icons/io';
import { IoStar, IoStarOutline } from 'react-icons/io5';
import InputField from '../../ui/components/InputField';
import DropDownMenu from '../../ui/components/DropDownMenu';
import NumberInputField from '../../ui/components/NumberInputField';
import SourceCard from '../../ui/components/SourceCard';
import { useNavigate } from 'react-router-dom';
import SidePanelScaffold from '../../ui/pageScaffolds/SidePanelScaffold';
import ChatTopBar from '../../ui/panels/TopBars/ChatTopBar';
import LoadingSpinner from '../../ui/components/LoadingSpinner';
import { LoginState } from '../../types/LoginState';
import BugButton from '../../ui/components/BugButton/BugButton';
import FeedBackButton from '../../ui/components/FeedBackButton/FeedBackButton';
import { Source } from '../../types/Source';
import settingsIcon from '../../images/settings-icon.svg';
import editIcon from '../../images/edit-icon.svg';
import { UserUtils } from '../../utils/UserUtils';
import ChatInputField from '../../ui/components/ChatInputField';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DifficultyBadge from '../ScienceSummaries/components/DifficultyBadge';
const isMock = process.env.REACT_APP_MOCK === 'true';


// import editIcon from '../images/edit-icon.svg';

interface ChatMessage {
  role: string;
  text: string;
  sources?: Source[];
}

interface ScienceSummary {
  difficulty: number;
  conclusion: string;
  keyConcepts: string;
  sixthGradeExplanation: string;
  highSchoolExplanation: string;
  collegeFreshmanExplanation: string;
}

interface TrimmedHistory {
  chatHistoryId: number;
  text: string;
  isPinned?: boolean;
}

interface ChatSetting {
  educationLevel: string;
  detailLevel: string;
  threshold: number;
}

interface BotResponse {
  chatText: string;
  sources: Source[];
}

interface ChatData {
  query: string;
  botResponse: BotResponse;
  chatMessages: ChatMessage[];
  chatId: number;
}

function ChatPage() {
  const token = localStorage.getItem('token'); // Retrieve the token
  const nav = useNavigate();
  const levels = ['Short', 'Medium', 'Long'];
  const [hide, setHide] = useState(true);
  const [loginState, setLoginState] = useState<LoginState>({
    isLoggedIn: false,
    isAdmin: false,
  });

  // Get current user name from token
  const currentUserName = UserUtils.getCurrentUserName();

  // Ref for auto-scrolling to bottom
  const chatScrollAreaRef = useRef<HTMLDivElement>(null);

  const validateUserLogin = async () => {
    if (token) {
      const url = '/validate';
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`, // Include the token in the Authorization header
        },
      });
      if (!response.ok) {
        localStorage.removeItem('token');
        nav('/');
        return;
      }
      const json = await response.json();

      if (json.valid && json.valid === true) {
        setLoginState({ isLoggedIn: true, isAdmin: true });
      } else {
        setLoginState({ isLoggedIn: true, isAdmin: false });
      }
    }
  };

  useEffect(() => {
    validateUserLogin();
  }, []);

  const [chatSettings, setChatSettings] = useState<ChatSetting>({
    educationLevel: 'A high school student',
    detailLevel: levels[0],
    threshold: 40,
  });

  const [chatData, setChatData] = useState<ChatData>({
    query: '',
    botResponse: { chatText: '', sources: [] },
    chatMessages: [],
    chatId: -1,
  });

  const [ws, setWs] = useState<WebSocket | null>(null);

  const [sideBarExpanded, setSideBarExpanded] = useState<boolean>(true);

  // Track currently displayed references
  const [currentReferences, setCurrentReferences] = useState<Source[]>([]);
  const [currentReferenceMessageIndex, setCurrentReferenceMessageIndex] =
    useState<number>(-1);
  const [showReferencesPanel, setShowReferencesPanel] = useState<boolean>(true);
  const [highlightedSourceIndex, setHighlightedSourceIndex] = useState<number>(-1);

  // Loading state for AI response generation
  const [isGenerating, setIsGenerating] = useState<boolean>(false);

  const [recentHistory, setRecentHistory] = useState<TrimmedHistory[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState(false);
  // TODO need to make a more robust method of selecting entities
  const [selectablePackages, setSelectablePackages] = useState<string[]>([]);
  const [selectedPackages, setSelectedPackages] = useState<string[]>([]);

  // Mode toggle: 'chat' or 'summary'
  const [mode, setMode] = useState<'chat' | 'summary'>('chat');
  const [summaries, setSummaries] = useState<ScienceSummary[]>([]);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [summaryError, setSummaryError] = useState('');
  const [selectedChat, setSelectedChat] = useState<number | null>(null);

  // const handleQueryChange = (event: ChangeEvent<HTMLInputElement>) => {
  // const handleQueryChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
  const handleQueryChange = (event: string) => {
    setChatData((prev) => ({
      ...prev,
      // query: event.target.value,
      query: event,
    }));
  };

  const handleEducationLevelChange = (event: ChangeEvent<HTMLInputElement>) => {
    setChatSettings({
      educationLevel: event.target.value,
      detailLevel: chatSettings.detailLevel,
      threshold: chatSettings.threshold,
    });
  };

  const handleThresholdChange = (value: number) => {
    setChatSettings({
      educationLevel: chatSettings.educationLevel,
      detailLevel: chatSettings.detailLevel,
      threshold: value,
    });
  };

  const handleDetailChange = (value: string) => {
    setChatSettings({
      educationLevel: chatSettings.educationLevel,
      detailLevel: value,
      threshold: chatSettings.threshold,
    });
  };

  const toggleSideBarExpansion = () => {
    setSideBarExpanded(!sideBarExpanded);
  };

  const getValidPackages = async () => {
    const url = '/validpackages';
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`, // Include the token in the Authorization header
        },
      });
      if (!response.ok) {
        if (response.status === 401) {
          nav('/signin');
        }
        throw new Error(`Network response was not ok`);
        // TODO should set a variable here
      }
      const json = await response.json();
      const packages: string[] = json;
      setSelectablePackages(packages);
      setSelectedPackages([packages[0]]); // TODO Critical, when updating with actual selection capability remove this.
    } catch (error) {
      // TODO should set a variable here
    }
  };

  const startWebSocket = (callback?: () => void) => {
    // Close the previous WebSocket if it's already open
    const mockEndpoint = isMock ? '/mock' : '';

    if (ws) {
      ws.close();
      setWs(null);
    }

    // Initialize the WebSocket connection
    let socket: WebSocket;
    if (process.env.REACT_APP_ENV === 'local') {
      socket = new WebSocket(`ws://localhost:3000/chatbot${mockEndpoint}`);
    } else {
      socket = new WebSocket(
        `${window.location.origin.replace(/^http/, 'ws')}/chatbot${mockEndpoint}`
      );
    }

    // Set the WebSocket on open connection and call the callback if provided
    socket.onopen = () => {
      if (token) {
        socket.send(JSON.stringify({ type: 'auth', token })); // Send the token as the first message
      }
      setWs(socket);
      if (callback) {
        callback(); // Send query only when WebSocket is open
      }
    };

    // Handle incoming messages
    socket.onmessage = (event: MessageEvent) => {
      const data = JSON.parse(event.data);

      switch (data.type) {
        case 'chatId':
          setChatData((prev) => ({
            ...prev,
            chatId: data.id!,
          }));
          setSelectedChat(data.id!);
          getRecentHistory();
          break;
        case 'aiResponseChunk':
          const newTextChunk: string = data.text;
          setIsGenerating(false); // Stop loading when first chunk arrives
          setChatData((prev) => ({
            ...prev,
            botResponse: {
              ...prev.botResponse,
              chatText: prev.botResponse.chatText + newTextChunk,
            },
          }));
          break;
        case 'messageEnd':
          setIsGenerating(false); // Ensure loading is stopped
          setChatData((prev) => ({
            ...prev,
            botResponse: { chatText: '', sources: [] },
            chatMessages: [
              ...prev.chatMessages,
              {
                role: 'system',
                text: prev.botResponse.chatText,
                sources: prev.botResponse.sources,
                showSources: true,
              },
            ],
          }));
          break;
        case 'noSources':
          setChatData((prev) => ({
            ...prev,
            botResponse: { ...prev.botResponse, sources: [] },
          }));
          break;
        case 'aiSources':
          if (data.sources) {
            try {
              const newSources = data.sources as Source[];
              setChatData((prev) => ({
                ...prev,
                botResponse: { ...prev.botResponse, sources: newSources },
              }));
            } catch (error) {
              // Optionally handle error
            }
          }
          break;
        case 'inputError':
          setIsGenerating(false); // Stop loading on error
          console.log(`an error occured with inputs`);
          // TODO prompt user that an error occured and to adjust inputs?
          break;
        case 'serverError':
          setIsGenerating(false); // Stop loading on error
          console.log(`an error occured with the server`);
          // TODO Tell user server had an issue and maybe try again later
          break;
        case 'authError':
          setIsGenerating(false); // Stop loading on error
          nav('/signin');
          break;
        default:
          console.error('Unknown message type:', data.type);
          console.log(data);
      }
    };

    socket.onerror = (error: Event) => {
      console.error('WebSocket error:', error);
    };

    // Cleanup WebSocket connection on component unmount
    return () => {
      socket.close();
    };
  };

  const sendQuery = () => {
    const model = 'deepseek-chat';
    if (ws && ws.readyState === WebSocket.OPEN) {
      const payload = {
        modelType: model,
        query: chatData.query,
        educationLevel: chatSettings.educationLevel,
        detailLevel: chatSettings.detailLevel,
        threshold: chatSettings.threshold,
        chatId: chatData.chatId === -1 ? null : chatData.chatId,
        selectedPackages: selectedPackages,
      };
      setIsGenerating(true); // Start loading indicator
      setChatData((prev) => ({
        ...prev,
        query: '',
        botResponse: { chatText: '', sources: [] },
        chatMessages: [
          ...prev.chatMessages,
          { role: 'user', text: chatData.query, sources: [] },
        ],
      }));
      ws.send(JSON.stringify(payload));
    }
  };

  const generateScienceSummary = async () => {
    if (chatData.query.trim() === '') return;

    setIsLoadingSummary(true);
    setSummaryError('');
    setSummaries([]);

    try {
      const response = await fetch('/science-summary/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          query: chatData.query,
          selectedPackageIds: selectedPackages,
          threshold: chatSettings.threshold,
        }),
      });

      if (response.status === 401) {
        nav('/signin');
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to generate summaries');
      }

      setSummaries(data.summaries);
      setSummaryError('');
    } catch (err: any) {
      setSummaryError(
        err.message || 'An error occurred while generating summaries'
      );
      setSummaries([]);
    } finally {
      setIsLoadingSummary(false);
    }
  };

  const handlePinToggle = async (chatHistoryId: number, currentPinStatus: boolean) => {
    try {
      const response = await fetch('/chat/history/pin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          chatHistoryId: chatHistoryId,
          isPinned: !currentPinStatus,
        }),
      });

      if (response.ok) {
        getRecentHistory();
      }
    } catch (error) {
      console.log("Error in toggling pin status:", error);
    }
  }

  const handleSubmit = () => {
    if (chatData.query.trim() === '') return; // Don't send empty queries

    // Check mode and execute accordingly
    if (mode === 'summary') {
      generateScienceSummary();
    } else {
      // Chat mode
      if (ws && ws.readyState === WebSocket.OPEN) {
        sendQuery();
      } else if (!ws || ws.readyState === WebSocket.CLOSED) {
        // Only start new WebSocket if none exists or it's closed
        startWebSocket(sendQuery);
      }
    }
    // If WebSocket is connecting, wait for it to open naturally
  };

  const getRecentHistory = async () => {
    const url = '/chat/history';
    setLoadingHistory(true);
    try {
      const jsonData = {
        maxHistorySize: 20,
      };
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`, // Include the token in the Authorization header
        },
        body: JSON.stringify(jsonData),
      });
      if (!response.ok) {
        if (response.status === 401) {
          nav('/signin');
        }

        throw new Error(`Network response was not ok`);
      }
      const json = await response.json();
      const history = JSON.parse(json) as TrimmedHistory[];
      setLoadingHistory(false);
      setRecentHistory(history);
    } catch (error) {
      setLoadingHistory(true);
      setHistoryError(true);
    }
  };

  const handleHistoryClicked = async (historyId: number) => {
    try {

      setSelectedChat(historyId);
      const url = `/chat/history/specific?chatHistoryId=${historyId}&maxHistorySize=20`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`, // Include the token in the Authorization header
        },
      });

      if (!response.ok) {
        throw Error(
          `There was an error with the history network call, ${response.status}`
        );
      }

      const json: { chatRole: string; chatText: string; sources?: Source[] }[] =
        await response.json();

      const chatMessages: ChatMessage[] = json.map((obj) => ({
        role: obj.chatRole,
        text: obj.chatText,
        sources: obj.sources || [],
      }));
      chatMessages.reverse();
      setIsGenerating(false); // Reset loading state when viewing history
      setChatData({
        query: '',
        botResponse: { chatText: '', sources: [] },
        chatMessages: chatMessages,
        chatId: historyId,
      });
    } catch (error) {
      console.log(`An error occurred:`);
      console.log(error);
    }
  };

  const handleNewChatClicked = async () => {
    setIsGenerating(false); // Reset loading state
    setSelectedChat(null);
    setChatData({
      query: '',
      botResponse: { chatText: '', sources: [] },
      chatMessages: [],
      chatId: -1,
    });
    await getRecentHistory();
  };

  const handleSetSelectedPackage = async (item: string) => {
    // TODO, when setting up selecting multiple packages for real, this will need to be changed
    if (selectablePackages.includes(item)) {
      setSelectedPackages([item]);
    } else {
      // TODO remove and replace with an actual error notification for user, or omit it and handle in some other way.
      console.log(
        `Somehow an incorrect package was selected, are you doing something strange?`
      );
    }
  };

  // Get required background info
  useEffect(() => {
    getValidPackages();
    getRecentHistory();
  }, []);

  // Set up the WebSocket on component mount
  useEffect(() => {
    startWebSocket();
    return () => {
      if (ws) {
        ws.close();
      }
    };
  }, []);

  // Auto-scroll to bottom when new messages arrive or bot is responding
  useEffect(() => {
    if (chatScrollAreaRef.current) {
      chatScrollAreaRef.current.scrollTo({
        top: chatScrollAreaRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [chatData.chatMessages, chatData.botResponse.chatText]);

  // Update references panel with latest sources
  useEffect(() => {
    // Show sources from actively streaming response
    if (
      chatData.botResponse.chatText &&
      chatData.botResponse.sources.length > 0
    ) {
      setCurrentReferences(chatData.botResponse.sources);
      setCurrentReferenceMessageIndex(-2); // -2 indicates currently streaming message
    }
    // Or show sources from the last system message
    else if (chatData.chatMessages.length > 0) {
      const reversedMessages = [...chatData.chatMessages].reverse();
      const lastSystemMessageIndex = reversedMessages.findIndex(
        (msg) => msg.role === 'system' && msg.sources && msg.sources.length > 0
      );

      if (lastSystemMessageIndex !== -1) {
        const actualIndex =
          chatData.chatMessages.length - 1 - lastSystemMessageIndex;
        const lastSystemMessage = chatData.chatMessages[actualIndex];
        if (lastSystemMessage.sources) {
          setCurrentReferences(lastSystemMessage.sources);
          setCurrentReferenceMessageIndex(actualIndex);
        }
      }
    } else {
      // No messages with sources
      setCurrentReferences([]);
      setCurrentReferenceMessageIndex(-1);
    }
    // Reset highlight when references change
    setHighlightedSourceIndex(-1);
  }, [chatData.botResponse, chatData.chatMessages]);

  const handleViewReferences = (messageIndex: number, sources: Source[]) => {
    // If clicking on the same message that's already showing, hide the panel
    if (currentReferenceMessageIndex === messageIndex && showReferencesPanel) {
      setShowReferencesPanel(false);
      setCurrentReferenceMessageIndex(-1);
      setCurrentReferences([]);
    } else {
      // Show references for the clicked message
      setCurrentReferences(sources);
      setCurrentReferenceMessageIndex(messageIndex);
      setShowReferencesPanel(true);
    }
  };

  const handleHide = () => {
    setHide(!hide);
  };

  const pinnedChats = (recentHistory || []).filter(c => c.isPinned === true);
  const remainingChats = (recentHistory || []).filter(c => !c.isPinned);

  return (
    <SidePanelScaffold
      isLoggedIn={loginState.isLoggedIn}
      isAdmin={loginState.isAdmin}
      stickyTopBar={true}
      isExpanded={sideBarExpanded}
      showCollapseIcons={true}
      onExpandedToggle={toggleSideBarExpansion}
      userName={currentUserName}
      sideBarHeaderChildren={
        <div className="new-chatbot" onClick={handleNewChatClicked}>
          <span>
            New Chat <img src={editIcon} alt="Edit Icon" />
          </span>
        </div>
      }
      topBarChildren={
        <ChatTopBar
          isSticky={true}
          isLoggedIn={loginState.isLoggedIn}
          showAdminButton={loginState.isAdmin}
          selectablePackages={selectablePackages}
          selectedPackages={selectedPackages}
          onPackageSelect={handleSetSelectedPackage}
          userName={currentUserName}
          onSettingsClick={handleHide}
        />
      }
      sideBarChildren={
        <div>
          {/* We should probably move this out to be it's own component */}
          <div className="ChatListContainer">
            {loadingHistory ? (
              <LoadingSpinner />
            ) : (
              <ul className="chat-history">
                {pinnedChats.length > 0 && (
                  <>
                    <p className="pinned-chat-head">Starred Chats</p>
                    {pinnedChats.map((chat) => (
                      <li
                        key={chat.chatHistoryId}
                        className={selectedChat === chat.chatHistoryId ? 'selectedChat' : ''}
                        onClick={() => handleHistoryClicked(chat.chatHistoryId)}
                      >

                        <div className="chat-history-item">
                          <span>{chat.text}</span>
                          <button className="chat-pin-btn" onClick={(e) => {
                            e.stopPropagation();
                            handlePinToggle(chat.chatHistoryId, chat.isPinned || false);
                          }}>
                            {chat.isPinned ? (
                              <IoStar className="star-icon pinned" />
                            ) : (
                              <IoStarOutline className="star-icon unpinned" />
                            )}
                          </button>
                        </div>

                      </li>
                    ))}
                    <p className="pinned-chat-head">Recent Chats</p>
                  </>
                )}
                {remainingChats.map((chat) => (
                  <li
                    key={chat.chatHistoryId}
                    className={selectedChat === chat.chatHistoryId ? 'selectedChat' : ''}
                    onClick={() => handleHistoryClicked(chat.chatHistoryId)}
                  >

                    <div className="chat-history-item">
                      <span>{chat.text}</span>
                      <button className="chat-pin-btn" onClick={(e) => {
                        e.stopPropagation();
                        handlePinToggle(chat.chatHistoryId, chat.isPinned || false);
                      }}>
                        {chat.isPinned ? (
                          <IoStar className="star-icon pinned" />
                        ) : (
                          <IoStarOutline className="star-icon unpinned" />
                        )}
                      </button>
                    </div>

                  </li>
                ))}
              </ul>
            )}
          </div>
          {/* TODO @Furqan, fix this, there is no show more shown. If you do not want it on the page, remove it */}
          {/* If we are keeping it, make it expand the amount of history that will show in trimmed history, and allow the history to then be scrollable */}
          <div className="show-more">
            <span
              onClick={() => {
                nav('/chat/history/search');
              }}
            >
              Show More
            </span>
          </div>
        </div>
      }
      mainPageChildren={
        <div className="main-chat-container">
          <div className="primaryChatContainer">
            <div className="ChatPageContentWrapper">
              {/* Chat Area */}
              <div className="ChatPageMainWrapper">
                <div className="ChatPageChatArea" ref={chatScrollAreaRef}>
                  {/* Summary Mode Display */}
                  {mode === 'summary' && (
                    <div className="summary-results-container">
                      {summaries.length === 0 &&
                        !isLoadingSummary &&
                        !summaryError && (
                          <div className="defaultScreen">
                            <img
                              src={'/video-search_Icon.png'}
                              alt="Logo"
                              className="logo"
                            />
                            <h4 className="defaultScreenName">
                              Science Summary Mode
                            </h4>
                            <p className="summary-hint">
                              Enter a scientific topic to analyze content from
                              your packages
                            </p>
                          </div>
                        )}

                      {isLoadingSummary && (
                        <div className="loading-summary">
                          <LoadingSpinner />
                          <p>Analyzing scientific content...</p>
                        </div>
                      )}

                      {summaryError && (
                        <div className="summary-error">
                          <p>{summaryError}</p>
                        </div>
                      )}

                      {summaries.length > 0 && (
                        <div className="summaries-display">
                          <div className="summaries-header">
                            <h2>Analysis Results</h2>
                            <p>
                              Found {summaries.length} key conclusion
                              {summaries.length !== 1 ? 's' : ''}
                            </p>
                          </div>
                          <div className="summaries-table-wrapper">
                            <table className="summaries-table">
                              <thead>
                                <tr>
                                  <th>Difficulty</th>
                                  <th>Conclusion</th>
                                  <th>Key Concepts</th>
                                  <th>6th Grade</th>
                                  <th>High School</th>
                                  <th>College</th>
                                </tr>
                              </thead>
                              <tbody>
                                {summaries.map((summary, index) => (
                                  <tr key={index}>
                                    <td className="difficulty-cell">
                                      <DifficultyBadge
                                        level={summary.difficulty}
                                      />
                                    </td>
                                    <td className="conclusion-cell">
                                      {summary.conclusion}
                                    </td>
                                    <td className="concepts-cell">
                                      {summary.keyConcepts}
                                    </td>
                                    <td className="explanation-cell">
                                      {summary.sixthGradeExplanation}
                                    </td>
                                    <td className="explanation-cell">
                                      {summary.highSchoolExplanation}
                                    </td>
                                    <td className="explanation-cell">
                                      {summary.collegeFreshmanExplanation}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Chat Mode Display */}
                  {mode === 'chat' &&
                    (chatData.chatMessages.length === 0 &&
                      !chatData.botResponse.chatText ? (
                      <div className="defaultScreen">
                        <img
                          src={'/video-search_Icon.png'}
                          alt="Logo"
                          className="logo"
                        />
                        <h4 className="defaultScreenName">Video Search AI</h4>
                      </div>
                    ) : (
                      <div className="ChatScrollArea">
                        {chatData.chatMessages.map((msg, i) => (
                          <div
                            key={i}
                            className={`chat-message ${msg.role} ${currentReferenceMessageIndex === i &&
                              showReferencesPanel
                              ? 'message-references-active'
                              : ''
                              }`}
                          >
                            <div className="message-content">
                              <div className="markdown-content">
                                <ReactMarkdown
                                  remarkPlugins={[remarkGfm]}
                                  components={{
                                    a: ({ node, href, children, ...props }) => {
                                      // Check if this is a source link by checking if href matches a source URL
                                      if (msg.role === 'system' && msg.sources && msg.sources.length > 0) {
                                        // Get link text as string (handle React nodes)
                                        const linkText = typeof children === 'string'
                                          ? children
                                          : (Array.isArray(children) ? children.join('') : String(children));

                                        const sourceIndex = msg.sources.findIndex(
                                          (source) => {
                                            const urlMatch = source.sourceUrl === href || (href === '#' && (source.sourceUrl === null || source.sourceUrl === ''));
                                            const titleMatch = source.sourceTitle === linkText.trim();
                                            return urlMatch || titleMatch;
                                          }
                                        );
                                        if (sourceIndex !== -1) {
                                          return (
                                            <a
                                              {...props}
                                              href={href}
                                              onClick={(e) => {
                                                e.preventDefault();
                                                // Set the highlighted source index
                                                setHighlightedSourceIndex(sourceIndex);
                                                // Ensure references panel is visible
                                                if (!showReferencesPanel) {
                                                  setShowReferencesPanel(true);
                                                }
                                                // Scroll to the source card if needed
                                                setTimeout(() => {
                                                  const sourceCard = document.querySelector(
                                                    `[data-source-index="${sourceIndex}"]`
                                                  );
                                                  if (sourceCard) {
                                                    sourceCard.scrollIntoView({
                                                      behavior: 'smooth',
                                                      block: 'center',
                                                    });
                                                  }
                                                }, 100);
                                              }}
                                              style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                            >
                                              {children}
                                            </a>
                                          );
                                        }
                                      }
                                      // Default link behavior for non-source links
                                      return (
                                        <a {...props} href={href} target="_blank" rel="noopener noreferrer">
                                          {children}
                                        </a>
                                      );
                                    },
                                  }}
                                >
                                  {(() => {
                                    let messageText = msg.text;
                                    if (
                                      msg.role === 'system' &&
                                      msg.sources &&
                                      msg.sources.length > 0
                                    ) {
                                      messageText +=
                                        '\n\n---\n\n**Sources:**\n\n' +
                                        msg.sources
                                          .map(
                                            (source, idx) =>
                                              `${idx + 1}. [${source.sourceTitle}](${source.sourceUrl || '#'})`
                                          )
                                          .join('\n');
                                    }
                                    return messageText;
                                  })()}
                                </ReactMarkdown>
                              </div>
                              {msg.role === 'system' &&
                                msg.sources &&
                                msg.sources.length > 0 && (
                                  <div className="message-references-indicator">
                                    <button
                                      className="view-references-btn"
                                      onClick={() =>
                                        handleViewReferences(i, msg.sources!)
                                      }
                                    >
                                      {currentReferenceMessageIndex === i &&
                                        showReferencesPanel
                                        ? 'Hide References'
                                        : `Show ${msg.sources.length} Reference${msg.sources.length > 1 ? 's' : ''}`}
                                    </button>
                                  </div>
                                )}
                            </div>
                          </div>
                        ))}
                        {isGenerating &&
                          chatData.botResponse.chatText === '' && (
                            <div className="chat-message system typing-indicator-container">
                              <div className="typing-indicator">
                                <span></span>
                                <span></span>
                                <span></span>
                              </div>
                            </div>
                          )}
                        {chatData.botResponse.chatText != '' && (
                          <div
                            className={`chat-message system ${currentReferenceMessageIndex === -2 &&
                              showReferencesPanel
                              ? 'message-references-active'
                              : ''
                              }`}
                          >
                            <div className="message-content">
                              <div className="markdown-content">
                                <ReactMarkdown
                                  remarkPlugins={[remarkGfm]}
                                  components={{
                                    a: ({ node, href, children, ...props }) => {
                                      // Check if this is a source link for streaming response
                                      if (chatData.botResponse.sources && chatData.botResponse.sources.length > 0) {
                                        // Get link text as string (handle React nodes)
                                        const linkText = typeof children === 'string'
                                          ? children
                                          : (Array.isArray(children) ? children.join('') : String(children));

                                        const sourceIndex = chatData.botResponse.sources.findIndex(
                                          (source) => {
                                            const urlMatch = source.sourceUrl === href || (href === '#' && (source.sourceUrl === null || source.sourceUrl === ''));
                                            const titleMatch = source.sourceTitle === linkText.trim();
                                            return urlMatch || titleMatch;
                                          }
                                        );
                                        if (sourceIndex !== -1) {
                                          return (
                                            <a
                                              {...props}
                                              href={href}
                                              onClick={(e) => {
                                                e.preventDefault();
                                                setHighlightedSourceIndex(sourceIndex);
                                                if (!showReferencesPanel) {
                                                  setShowReferencesPanel(true);
                                                }
                                                setTimeout(() => {
                                                  const sourceCard = document.querySelector(
                                                    `[data-source-index="${sourceIndex}"]`
                                                  );
                                                  if (sourceCard) {
                                                    sourceCard.scrollIntoView({
                                                      behavior: 'smooth',
                                                      block: 'center',
                                                    });
                                                  }
                                                }, 100);
                                              }}
                                              style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                            >
                                              {children}
                                            </a>
                                          );
                                        }
                                      }
                                      return (
                                        <a {...props} href={href} target="_blank" rel="noopener noreferrer">
                                          {children}
                                        </a>
                                      );
                                    },
                                  }}
                                >
                                  {chatData.botResponse.chatText}
                                </ReactMarkdown>
                              </div>
                              {chatData.botResponse.sources.length > 0 && (
                                <div className="message-references-indicator">
                                  <span className="references-loading-indicator">
                                    ✓ References loaded (
                                    {chatData.botResponse.sources.length})
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                </div>
                {/* Query Input - Moved inside ChatPageMainWrapper */}
                <div className="ChatSearchContainer">
                  <div className="ChatPageQuery">
                    <ChatInputField
                      value={chatData.query}
                      label={
                        mode === 'chat'
                          ? 'Write query here...'
                          : 'Enter scientific topic or question...'
                      }
                      onValueChange={handleQueryChange}
                      onEnterPress={handleSubmit}
                    ></ChatInputField>
                    <IoMdSend
                      onClick={handleSubmit}
                      className="ChatPageConfirmButton"
                    />
                  </div>
                  {/* Settings Modal Backdrop */}
                  {!hide && (
                    <div
                      className="ChatPageSettingBackdrop"
                      onClick={() => setHide(true)}
                    ></div>
                  )}
                  {/* Settings Modal */}
                  <div className={`ChatPageSetting ${hide ? 'hidden' : ''}`}>
                    <div className="ChatPageSettingHeader">
                      <h3 className="ChatPageSettingTitle">Chat Settings</h3>
                      <button
                        className="ChatPageSettingCloseButton"
                        onClick={() => setHide(true)}
                        aria-label="Close settings"
                      >
                        <IoMdClose />
                      </button>
                    </div>
                    <div>
                      <p>Education Level:</p>
                      <InputField
                        value={chatSettings.educationLevel}
                        label={'Education Level'}
                        onValueChange={handleEducationLevelChange}
                      ></InputField>
                    </div>
                    <div>
                      <p> Detail Amount: </p>
                      <DropDownMenu
                        choices={levels}
                        onSelect={(value) => handleDetailChange(value)}
                      ></DropDownMenu>
                    </div>
                    <div>
                      <p>Relevancy Threshold:</p>
                      <NumberInputField
                        value={chatSettings.threshold}
                        label={'Threshold'}
                        onValueChange={handleThresholdChange}
                      ></NumberInputField>
                    </div>
                  </div>
                </div>
              </div>

              {/* References Panel - Moved to Right */}
              {currentReferences.length > 0 && showReferencesPanel && (
                <div className="ChatPageReferencesPanel">
                  <div className="references-panel-header">
                    <h3>References</h3>
                    <button
                      className="references-panel-close"
                      onClick={() => setShowReferencesPanel(false)}
                      aria-label="Close references"
                    >
                      <IoMdClose />
                    </button>
                  </div>
                  <div className="references-panel-content">
                    {currentReferences.map((source, index) => (
                      <div
                        key={index}
                        data-source-index={index}
                        className={highlightedSourceIndex === index ? 'source-card-highlighted' : ''}
                      >
                        <SourceCard source={source} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {/* <BugButton parentPageUrl={'/chat'} token={token} nav={nav} />
            <FeedBackButton parentPageUrl={'/chat'} token={token} nav={nav} /> */}
          </div>
        </div>
      }
    ></SidePanelScaffold>
  );
}

export default ChatPage;
